import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, BookOpen, Trash2, Search } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';
import { useTranslation } from 'react-i18next';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';
import GLAccountTree from './GLAccountTree';

const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

const GLAccountDetails = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const { canUpdate, canDelete } = usePermissions('gl-accounts');
  const { fontSize } = useFontSize();

  const session = JSON.parse(localStorage.getItem('user') || '{}');

  const [account, setAccount] = useState({
    glaccount: '', description: '', account_type: 'EXPENSE',
    parent_account: '', org_id: session.org_id || '', is_active: true
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', field: '' });

  const loadData = useCallback(async () => {
    if (isNew) {
      setAccount(prev => ({ ...prev, org_id: session.org_id || '' }));
      return;
    }
    try {
      const res = await api.get(`/gl/accounts/${id}`);
      setAccount(res.data);
    } catch (err) {
      toast.error(t('common.load_error', 'Failed to load account'));
    }
  }, [id, isNew]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    if (!account.glaccount) return toast.error(t('gl.accounts.code_required', 'Account code is required'));
    const lid = toast.loading(t('common.saving', 'Saving...'));
    try {
      await api.post('/gl/accounts/save', account);
      toast.success(t('toasts.save_success', 'Saved!'), { id: lid });
      if (isNew) navigate(`/gl-accounts/${account.glaccount}`);
      else loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toasts.save_fail', 'Save failed'), { id: lid });
    }
  };

  const handleDelete = async () => {
    if (isNew) return;
    const result = await Swal.fire({
      ...swalTheme(),
      title: t('common.are_you_sure', 'Are you sure?'),
      text: t('gl.accounts.delete_warn', 'This account will be permanently deleted.'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('common.delete', 'Delete')
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/gl/accounts/${account.glaccount}`);
      toast.success(t('common.delete_success', 'Deleted'));
      navigate('/gl-accounts');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.delete_fail', 'Delete failed'));
    }
  };

  return (
    <div className={`flex flex-col h-full bg-surface-app uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <GLAccountTree
        isOpen={lookup.isOpen}
        title={lookup.title}
        orgId={account.org_id}
        allowParentSelect
        excludeCode={account.glaccount}
        onClose={() => setLookup(prev => ({ ...prev, isOpen: false }))}
        onSelect={(node) => {
          setAccount(prev => ({ ...prev, [lookup.field]: node.glaccount }));
          setLookup(prev => ({ ...prev, isOpen: false }));
        }}
      />
      <div className="bg-primary-dark text-white p-3 flex justify-between items-center shadow-lg z-20">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/gl-accounts')} className="p-1.5 hover:bg-white/10 rounded-full">
            <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} />
          </button>
          <BookOpen size={18} className="text-status-warning" />
          <span className="font-black italic">{isNew ? t('gl.accounts.new', 'New Account') : account.glaccount}</span>
        </div>
        <div className="flex items-center gap-2">
          {canUpdate && (
            <button onClick={handleSave} className="bg-status-success px-6 py-1.5 rounded-sm font-black text-[11px] flex items-center gap-2 uppercase shadow-md transition-all">
              <Save size={14}/> {t('common.save', 'Save')}
            </button>
          )}
          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 hover:bg-status-danger/20 rounded text-status-danger transition-colors" title={t('common.delete', 'Delete')}>
              <Trash2 size={16}/>
            </button>
          )}
        </div>
      </div>

      <div className="p-8 max-w-4xl mx-auto w-full space-y-6 overflow-y-auto custom-scrollbar">
        <div className="bg-surface-card p-8 rounded-sm shadow-sm border-t-4 border-primary grid grid-cols-3 gap-10">
          <div className="flex flex-col gap-1 border-b border-line-subtle pb-2">
            <label className="text-[10px] font-black text-status-danger uppercase tracking-widest">{t('gl.accounts.fields.code', 'Account Code')} *</label>
            <input disabled={!isNew} className="text-lg font-black text-primary outline-none uppercase disabled:opacity-60"
                   value={account.glaccount} onChange={e => setAccount({...account, glaccount: e.target.value.toUpperCase()})} />
          </div>
          <div className="col-span-2 flex flex-col gap-1 border-b border-line-subtle pb-2">
            <label className="text-[10px] font-black text-ink-secondary uppercase tracking-widest">{t('gl.accounts.fields.description', 'Description')}</label>
            <input className="text-lg font-bold text-ink outline-none"
                   value={account.description || ''} onChange={e => setAccount({...account, description: e.target.value})} />
          </div>
        </div>

        <div className="bg-surface-card p-8 rounded-sm shadow-sm border grid grid-cols-3 gap-12">
          <div className="flex flex-col gap-2">
            <label className="text-[10px] font-black text-ink-secondary uppercase">{t('gl.accounts.fields.type', 'Account Type')}</label>
            <select className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1 cursor-pointer focus:border-status-warning transition-all"
                    value={account.account_type || 'EXPENSE'} onChange={e => setAccount({...account, account_type: e.target.value})}>
              {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[10px] font-black text-ink-secondary uppercase">{t('gl.accounts.fields.parent', 'Parent Account')}</label>
            <div
              className="flex items-center justify-between border-b-2 border-line-subtle py-1 group focus-within:border-status-warning transition-all cursor-pointer"
              onClick={() => setLookup({ isOpen: true, title: t('gl.accounts.fields.parent', 'Parent Account'), field: 'parent_account' })}
            >
              <span className={`font-bold uppercase ${account.parent_account ? 'text-primary' : 'text-ink-secondary italic font-normal'}`}>{account.parent_account || 'Select...'}</span>
              <Search size={14} className="text-primary shrink-0" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[10px] font-black text-ink-secondary uppercase">{t('gl.accounts.fields.org', 'Organization')}</label>
            <input className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1 uppercase focus:border-status-warning transition-all"
                   value={account.org_id || ''} onChange={e => setAccount({...account, org_id: e.target.value.toUpperCase()})} />
          </div>
          <label className="flex items-center gap-2 col-span-3 cursor-pointer">
            <input type="checkbox" checked={!!account.is_active} onChange={e => setAccount({...account, is_active: e.target.checked})} />
            <span className="text-[10px] font-black text-ink-secondary uppercase">{t('gl.accounts.fields.active', 'Active')}</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default GLAccountDetails;
